import * as React from "react"

export const Tabs = ({ defaultValue, className, children, ...props }: {
  defaultValue?: string
  className?: string
  children: React.ReactNode
} & React.HTMLAttributes<HTMLDivElement>) => {
  const [activeTab, setActiveTab] = React.useState(defaultValue || '')

  return (
    <div className={className} {...props}>
      {React.Children.map(children, child => {
        if (React.isValidElement(child)) {
          if (child.type === TabsList) {
            return React.cloneElement(
              child as React.ReactElement<{
                activeTab?: string
                onTabChange?: (value: string) => void
              }>,
              { activeTab, onTabChange: setActiveTab }
            )
          }
          if (child.type === TabsContent) {
            return React.cloneElement(
              child as React.ReactElement<{ activeTab?: string }>,
              { activeTab }
            )
          }
        }
        return child
      })}
    </div>
  )
}

export const TabsList = ({ className, children, activeTab, onTabChange, ...props }: {
  className?: string
  children: React.ReactNode
  activeTab?: string
  onTabChange?: (value: string) => void
} & React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={`inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground ${className || ''}`}
    {...props}
  >
    {React.Children.map(children, child => {
      if (React.isValidElement(child) && child.type === TabsTrigger) {
        return React.cloneElement(
          child as React.ReactElement<{
            activeTab?: string
            onTabChange?: (value: string) => void
          }>,
          { activeTab, onTabChange }
        )
      }
      return child
    })}
  </div>
)

export const TabsTrigger = ({ className, children, value, activeTab, onTabChange, ...props }: {
  className?: string
  children: React.ReactNode
  value: string
  activeTab?: string
  onTabChange?: (value: string) => void
} & React.HTMLAttributes<HTMLButtonElement>) => {
  const isActive = activeTab === value
  
  return (
    <button
      className={`inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 ${
        isActive 
          ? 'bg-background text-foreground shadow-sm' 
          : 'hover:bg-background/50'
      } ${className || ''}`}
      onClick={() => onTabChange?.(value)}
      {...props}
    >
      {children}
    </button>
  )
}

export const TabsContent = ({ className, children, value, activeTab, ...props }: {
  className?: string
  children: React.ReactNode
  value: string
  activeTab?: string
} & React.HTMLAttributes<HTMLDivElement>) => {
  if (activeTab !== value) return null
  
  return (
    <div
      className={`mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${className || ''}`}
      {...props}
    >
      {children}
    </div>
  )
} 